<script lang="ts">
    import { afterUpdate, getContext, onMount } from 'svelte';
    import PresentationContext from 'Pages/PresentationPage/Lib/PresentationContext';
    import DependencyContainer from 'Lib/DependencyContainer';
    import { applicationContextKey } from 'PublicationDependencies';
    import Button from 'Components/Button.svelte';
    import translations from 'Assets/i18n';

    const applicationContext = getContext<DependencyContainer>(applicationContextKey);
    const { containerLogsObservable } = applicationContext.get(PresentationContext);

    const logLeveRegex = /\[.+ (.{3})\]/;

    let divElement: HTMLDivElement | undefined;
    let scrolledToEnd = true;

    function scrollToEndIfNeeded() {
        if (divElement !== undefined && scrolledToEnd) {
            scrollToEnd();
        }
    }

    function checkIfScrolledToEnd() {
        if (divElement !== undefined) {
            if (Math.abs(divElement.scrollHeight - divElement.scrollTop - divElement.clientHeight) < 1) {
                return true;
            }
        }
        return false;
    }

    function scrollToEnd() {
        if (divElement !== undefined) {
            divElement.scrollTop = divElement.scrollHeight;
        }
    }

    afterUpdate(() => {
        scrollToEndIfNeeded();
    });

    onMount(() => {
        divElement?.addEventListener('scroll', event => {
            scrolledToEnd = checkIfScrolledToEnd();
        });
        scrollToEndIfNeeded();
    });

    function getLogLevelRuleFromLine(input: string) {
        const match = logLeveRegex.exec(input);
        if (match != null) {
            const level = match[1];

            switch (level) {
                case 'FTL':
                    return 'fatal';
                case 'ERR':
                    return 'error';
                case 'WRN':
                    return 'warn';
                case 'INF':
                    return 'info';
                case 'DBG':
                    return 'debug';
                case 'VRB':
                    return 'verbose';
            }
        }
        return '';
    }

    function downloadLogs(){
        const blob = new Blob([$containerLogsObservable ?? ""], {type: 'text/plain'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'presentation_logs.txt';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
</script>

<div
    class="log-container"
    bind:this={divElement}>
    <div class="button-holder">
        <Button
            minWidth="32px"
            padding="0"
            backgroundColor="transparent"
            tooltipContent={translations.pages.logViewer.downloadLogs}
            on:click={downloadLogs}>
            <span class="mdi mdi-download"/>
        </Button>
    </div>
    {#each $containerLogsObservable?.split('\n') ?? [] as line}
        <span class="entry {getLogLevelRuleFromLine(line)}">{line}</span>
    {/each}
</div>

<style>
    .log-container {
        padding: 8px;
        height: 100%;
        width: 100vw;
        background-color: #0A0E14;
        color: #B3B1AD;
        overflow-y: scroll;
    }

    .entry {
        display: block;
        line-height: 1.3rem;
        white-space: pre-wrap;
    }

    .fatal {
        color: #FF3333;
        font-weight: bold;
    }

    .error {
        color: #FF3333;
    }

    .warn {
        color: #FF8F40;
    }

    .info {
        color: #FFEE99;
    }

    .debug {
        color: #59C2FF;
    }

    .verbose {
        color: #95E6CB;
    }

    .button-holder {
        float: right;
    }

    .mdi {
        font-size: 32px;
    }
</style>
